import type { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import {
    compareAnswerOptionsByModuleOrder,
    isMultipleChoiceQuestion,
} from "models/AnswerType";
import type Question from "models/Question";
import { useEffect } from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import type { CategoricalGroupByConfiguration } from "../../../SignalsModels";

export type QuestionResultModuleOption = {
    id: string;
    label: string;
    categoryName: string;
};

export type QuestionResultQuestionOption = {
    id: string;
    label: string;
};

export type QuestionResultGroupingOptions = {
    /** Modules the viewer may group by, grouped for the picker by module category. */
    moduleGroups: { label: string; options: QuestionResultModuleOption[] }[];
    modulesLoading: boolean;
    /** Multiple-choice questions on the selected module, in module order. */
    questionOptions: QuestionResultQuestionOption[];
    questionsLoading: boolean;
    /** Answer options for the selected question, in the order the module configures them. */
    tagOptions: { id: string; label: string }[];
};

const getModuleLabel = (module: ModulePreview) => {
    const description = module.description?.trim();
    return description ? `${module.name} - ${description}` : module.name;
};

/**
 * Whether a question can be grouped on.
 *
 * `loadModuleDetails` flattens the module's question tree when it caches it, so a question nested
 * under a Question Group is already in the list and needs no traversal here.
 * `isMultipleChoiceQuestion` is the same test the Question Result filter applies.
 */
const isGroupableQuestion = (question: Question): boolean =>
    !!question.id && isMultipleChoiceQuestion(question);

/**
 * Sources the module, question, and answer-option lists a Question Result grouping is configured
 * from.
 *
 * Reads the same stores the Question Result *filter* does, so the two offer the same modules under
 * the same permission rules and the same multiple-choice-only questions. A chart cannot be grouped
 * by something the filter would refuse. Values are read during render rather than memoized so MobX
 * tracks them, matching `useCategoricalValueOptions`.
 *
 * Returns empty lists for any other grouping option, so callers can use it unconditionally.
 */
export const useQuestionResultGroupingOptions = (
    draft: CategoricalGroupByConfiguration,
): QuestionResultGroupingOptions => {
    const filterValuesStore = useStore(ApplicationFilterValuesStore);

    const isQuestionResult = draft.option === "QuestionResult";
    const licensedModuleId = draft.licensedModuleId;
    const questionId = draft.questionId;

    useEffect(() => {
        if (!isQuestionResult) return;
        void filterValuesStore.loadQuestionResultFilterableModules();
    }, [filterValuesStore, isQuestionResult]);

    useEffect(() => {
        if (!isQuestionResult || !licensedModuleId) return;
        void filterValuesStore.loadModuleDetails(licensedModuleId);
    }, [filterValuesStore, isQuestionResult, licensedModuleId]);

    const moduleOptions: QuestionResultModuleOption[] = isQuestionResult
        ? filterValuesStore.questionResultFilterableModules.map((module) => ({
              id: module.id,
              label: getModuleLabel(module),
              categoryName: module.moduleCategory?.name ?? "Uncategorized",
          }))
        : [];

    const moduleGroups = (() => {
        const groups = new Map<string, QuestionResultModuleOption[]>();

        [...moduleOptions]
            .sort((left, right) => {
                const categoryCompare = left.categoryName.localeCompare(
                    right.categoryName,
                );
                if (categoryCompare !== 0) return categoryCompare;
                return left.label.localeCompare(right.label);
            })
            .forEach((module) => {
                const group = groups.get(module.categoryName) ?? [];
                group.push(module);
                groups.set(module.categoryName, group);
            });

        return Array.from(groups.entries()).map(([label, options]) => ({
            label,
            options,
        }));
    })();

    const moduleDetails =
        isQuestionResult && licensedModuleId
            ? filterValuesStore.moduleDetailsById.get(licensedModuleId)
            : undefined;

    const groupableQuestions = (moduleDetails?.questions ?? []).filter(
        isGroupableQuestion,
    );

    const questionOptions: QuestionResultQuestionOption[] =
        groupableQuestions.map((question) => ({
            id: question.id,
            label:
                question.shortQuestionText?.trim() ||
                question.questionText ||
                "Untitled question",
        }));

    const selectedQuestion = questionId
        ? groupableQuestions.find((question) => question.id === questionId)
        : undefined;

    const tagOptions = [...(selectedQuestion?.tags ?? [])]
        .filter((tag) => tag.isActive !== false)
        .sort(compareAnswerOptionsByModuleOrder)
        .map((tag) => ({ id: tag.id, label: tag.value }));

    return {
        moduleGroups,
        modulesLoading:
            isQuestionResult &&
            filterValuesStore.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_QUESTION_RESULT_MODULES,
            ),
        questionOptions,
        // "Not loaded yet" and "no groupable questions" look identical in the list, so the flag has
        // to come from the module details being absent rather than the list being empty.
        questionsLoading:
            isQuestionResult &&
            !!licensedModuleId &&
            !moduleDetails &&
            filterValuesStore.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_MODULE_DETAILS,
            ),
        tagOptions,
    };
};
